import { Injectable } from '@angular/core';
import { Observable, Subject, catchError, map, shareReplay, throwError } from 'rxjs';

import { apiPaths } from '../../app/core/constants/api.const';
import { AppUtilityService } from './app-utility.service';
import { StudyLogResponse, StudyLogbookApiResponse } from '../models/study-log.model';
import { toStudyLogResponse } from '../utils/study-log.util';

/**
 * StudyLogbookService
 *
 * One fetch of `GET studies/logbook/`, shared by every consumer. The endpoint returns all
 * studies for all years in a single payload, and both Studies tabs want the same list — the
 * Logbook to table it, Linked Studies to populate its picker and graph — so the response is
 * replayed rather than requested twice when the user moves between tabs.
 *
 * Saving a study changes what that payload should say, so the cache is dropped through
 * `invalidate()` and `invalidated$` tells any open Studies view to fetch the new one.
 */
@Injectable({ providedIn: 'root' })
export class StudyLogbookService {
  private logbook$: Observable<StudyLogResponse> | null = null;
  private readonly invalidated = new Subject<void>();

  /** Emits when the cached logbook has gone stale, so a view showing it can refetch. */
  readonly invalidated$ = this.invalidated.asObservable();

  constructor(private readonly appUtilityService: AppUtilityService) {}

  /** The logbook, fetched on first call and replayed to everyone after. */
  load(): Observable<StudyLogResponse> {
    if (!this.logbook$) {
      this.logbook$ = this.appUtilityService
        .get<StudyLogbookApiResponse>(this.appUtilityService.getAPI(apiPaths.study.logbook))
        .pipe(
          map(toStudyLogResponse),
          // shareReplay would otherwise hand the same failure to every later subscriber, so a
          // failed fetch drops the cache and the next caller starts a fresh request.
          catchError(error => {
            this.logbook$ = null;
            return throwError(() => error);
          }),
          shareReplay({ bufferSize: 1, refCount: false })
        );
    }
    return this.logbook$;
  }

  /** Discards the cached payload and fetches again — the Retry path. */
  reload(): Observable<StudyLogResponse> {
    this.logbook$ = null;
    return this.load();
  }

  /**
   * Marks the cached payload stale and tells open views to refetch — the path taken when a save
   * elsewhere in the app has changed what the logbook should say. Nothing is fetched here: a
   * view listening on `invalidated$` asks for the new payload, and one that is not open simply
   * fetches when it next loads.
   */
  invalidate(): void {
    this.logbook$ = null;
    this.invalidated.next();
  }
}
